import { Grid, Typography, FormControlLabel } from "@mui/material";
import "react-dropzone-uploader/dist/styles.css";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDateTimePicker } from "@mui/x-date-pickers/DesktopDateTimePicker";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import {
  getParams,
  navigate,
  sendGetRequest,
  sendPostRequest,
  sendPatchRequest,
  toTzTime,
  removeEmpty,
  allowedOperations,
  watchLiveData,
  generateUniqueId,
  capitalizeFirstLetter,
} from "../utils/utils";
import EditPage from "./component/edit-page";
import { useState, useEffect } from "react";
import AppRoutes from "../routing/routes";
import EasyTextEdit from "./component/text-edit";
import {
  Dropzone,
  FileMosaic,
  FullScreen,
  ImagePreview,
  VideoPreview,
} from "@files-ui/react";
import {
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  Autocomplete,
  TextField,
  Chip,
  FormHelperText,
  Switch,
  Checkbox,
} from "@mui/material";
import { coreStyles } from "../theme/style";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useSelector } from "react-redux";
import { set } from "lodash";

const ageGroup = [
  { range: "0-9", name: "Early child hood (AK,NN)" },
  { range: "7-14", name: "Primary (UK,NN)" },
  { range: "18-100", name: "Caregivers" },
];

const _ref = {
  uuid: "",
  section: "",
  thumbnail: "",
  order: "",
  code: "",
  title: "",
  subTitle: "",
  subject: "",
  category: "",
  forTheApp: true,
  forYoutube: null,
  language: "en",
  fromAge: null,
  toAge: null,
  premium: [],
  source: "s3",
  scheduledAt: null,
  logoRef: null,
  withIntroOutro: null,
  sourceType: "",
  streamQuality: ["240", "360", "480"],
};

const _showRef = {
  code: "",
  season: "",
  episode: "",
  segment: "",
};

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}));

const ContentEdit = () => {
  const appStyle = coreStyles();
  const { uuid } = getParams();
  var [content, setContent] = useState(_ref);
  var [sections, setSections] = useState([]);
  var [categories, setCategories] = useState([]);
  var [date, setDate] = useState(new Date());
  var [urlError, setUrlError] = useState(false);
  var [thumbError, setThumbError] = useState(false);
  var [files, setFiles] = useState([]);
  const [channels, setChannels] = useState([]);
  const [tvShows, setTvShows] = useState([]);
  const [continents, setContinents] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [imageSrc, setImageSrc] = useState(undefined);
  const [videoSrc, setVideoSrc] = useState(undefined);
  const [schedule, setSchedule] = useState(false);
  const [ageRange, setAgeRange] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const { canCreate } = allowedOperations();
  const [shows, setShows] = useState([]);
  const [showProps, setShowProps] = useState({ ..._showRef });
  const { queue } = useSelector((state) => state.queue);
  const [requestId, setRequestId] = useState(null);
  const [contentSourceTypes, setContentSourceTypes] = useState([]);

  const [status, setStatus] = useState({
    error: false,
    loading: false,
    message: null,
    progress: null,
  });

  const getAgeRanges = (start, end) => {
    const ranges = ageGroup.map((group) => {
      const parts = group.range.split("-");
      return { start: +parts[0], end: +parts[1] };
    });
    return ranges
      .filter((range) => range.start >= start && range.end <= end)
      .map((range) => `${range.start}-${range.end}`);
  };

  const getSingleRange = (values = null) => {
    const parts = (values || ageRange).map((range) => range.split("-")).flat(1);
    return `${Math.min(...parts)}-${Math.max(...parts)}`;
  };

  const updateFiles = (selectedFiles) => {
    const images = [],
      videos = [],
      docs = [],
      audio = [];
    selectedFiles.forEach((file) => {
      if (file.type.startsWith("image")) images.push(file);
      if (file.type.startsWith("video")) videos.push(file);
      if (file.type.startsWith("application")) docs.push(file);
      if (file.type.startsWith("audio")) docs.push(file);
    });

    if (
      images.length === 1 &&
      (videos.length === 1 || docs.length === 1 || audio.length === 1)
    ) {
      setFiles(selectedFiles);
    }
  };

  const onDelete = (id) => {
    setFiles(files.filter((_file) => _file.id !== id));
  };

  const handleSee = (imageSource) => {
    setImageSrc(imageSource);
  };
  const handleWatch = (videoSource) => {
    setVideoSrc(videoSource);
  };

  const validateYoutubeProps = () => {
    if (content.forYoutube === null) return true;
    return (
      content.forYoutube !== null &&
      content.forYoutube.channel &&
      content.forYoutube.fade
    );
  };

  const getMimeType = () => {
    try {
      const mimeTypes = ["image/*"];
      const name = `${categories.filter((cat) => cat.uuid === content.category)[0].name}`;
      if (name.toLowerCase().startsWith("video")) mimeTypes.push("video/*");
      if (name.toLowerCase().startsWith("audio")) mimeTypes.push("audio/*");
      if (name.toLowerCase().startsWith("books"))
        mimeTypes.push("application/pdf", "application/x-pdf");
      return mimeTypes;
    } catch (e) {
      return [];
    }
  };

  const checkLogoChennelError = () => {
    return (
      content.forYoutube !== null &&
      content.logoRef &&
      content.logoRef !== content.forYoutube.channel
    );
  };

  useEffect(() => {
    setStatus({ ...{ loading: true } });
    Promise.all([
      sendGetRequest("categories"),
      sendGetRequest("sections"),
      sendGetRequest(`channels`),
      sendGetRequest(`contents/continents`),
      sendGetRequest(`languages`),
      sendGetRequest(`subjects`),
      sendGetRequest(`shows`),
      sendGetRequest(`file-sources`),
    ]).then(
      ([
        categories,
        sections,
        channels,
        continents,
        languages,
        subjects,
        shows,
        sources,
      ]) => {
        setContentSourceTypes(sources);
        setContinents(continents);
        setLanguages(languages);
        setSubjects(subjects);
        setShows(shows);
        const types = categories.filter((type) => type.name !== "Home");
        types.sort((prev, next) => prev.order - next.order);
        setTvShows(channels);
        const _channels = channels.filter(
          (chan) => !chan.expired && chan.token,
        );
        setChannels(_channels);
        sendGetRequest(
          `contents?uuid=${uuid}`,
          (loading) => setStatus({ ...{ loading: loading } }),
          (data) => {
            if (data && data.forYoutube) {
              const _channel = _channels.filter(
                (chan) => chan.uuid === data.forYoutube.channel,
              );
              if (_channel.length === 0) {
                data.forYoutube = null;
              }
            }
            setCategories(types);
            setSections(sections);
            if (data) {
              const _category =
                uuid && data.section !== null && data.section.category
                  ? data.section.category.uuid
                  : categories[0]
                    ? categories[0].uuid
                    : null;
              const _section =
                uuid && data.section !== null
                  ? data.section.uuid
                  : categories.filter((cat) => cat.uuid === _category)[0]
                      .uuid || null;
              const _content = {
                ..._ref,
                ...data,
                subject: data.subject ? data.subject.uuid : null,
                section: _section,
                category: _category,
                source: "s3",
                thumbnail: data.urls.thumbnail.url,
                url: data.urls.source.url,
                sourceType: data.sourceType ? data.sourceType.uuid : null,
                withIntroOutro: data.processing.withIntroOutro || false,
              };
              setDate(
                _content.scheduledAt
                  ? new Date(_content.scheduledAt)
                  : new Date(),
              );
              const ranges = getAgeRanges(_content.fromAge, _content.toAge);
              setAgeRange(ranges);
              const processedRange = getSingleRange(ranges).split("-");
              if (_content.logoRef === "null") _content.logoRef = null;
              setContent({
                ..._content,
                fromAge: processedRange[0],
                toAge: processedRange[1],
              });
            }
          },
        );
      },
    );
    return () => setContent(_ref);
    // eslint-disable-next-line
  }, [uuid]);

  const noBranding = () =>
    content.logoRef == null ||
    content.logoRef === "null" ||
    content.logoRef === "none";

  const isVideo = () =>
    content.category === "1b367a19-9bcd-4b85-92e5-9bc80e835b59";

  const getContent = () => {
    content.isVideo = isVideo();
    if (!isVideo()) {
      delete content.streamQuality;
      delete content.withIntroOutro;
    }
    delete content.category;
    if (!content.order.length) delete content.order;

    if (!schedule) delete content.scheduledAt;
    if (noBranding()) content.logoRef = null;
    if (!content.streamQuality && isVideo())
      content.streamQuality = ["240", "360", "480"];
    delete content.uuid;
    if (content.source === "file") {
      delete content.url;
      delete content.thumbnail;
    }

    if (!content.premium.length || !content.premium) delete content.premium;

    const formData = new FormData();
    const _content = removeEmpty(content);
    for (const [key, value] of Object.entries({ ..._content })) {
      formData.append(
        key,
        (key === "premium" && value.length) || key === "forYoutube"
          ? JSON.stringify(value)
          : value,
      );
    }

    if (uuid) _content.uuid = uuid;

    if (files.length === 2) {
      files.forEach((file) => {
        formData.append("file", file.file);
      });
    }
    return {
      form: formData,
      contents: content,
    };
  };

  useEffect(() => {
    // eslint-disable-next-line
  }, [requestId]);

  useEffect(() => {
    if (queue) {
      const newRequestId = generateUniqueId();
      if (!requestId) setRequestId(newRequestId);
      watchLiveData(queue, newRequestId, ({ requestId: id, progress }) => {
        if (id === newRequestId) {
          setStatus({ ...status, progress, loading: true });
        }
      });
    }

    // eslint-disable-next-line
  }, [queue]);

  const createUpdate = () => {
    if (!uuid) {
      sendPostRequest(
        "contents",
        getContent().form,
        (loading) =>
          setStatus({
            ...{ loading: loading, progress: files.length === 2 ? 0 : null },
          }),
        (data) => {
          setStatus({
            ...{
              error: data.error,
              message: data.message,
            },
          });
        },
        requestId,
      );
    } else {
      sendPatchRequest(
        `contents/${uuid}`,
        getContent().contents,
        (loading) => setStatus({ ...{ loading: loading } }),
        (data) => {
          setStatus({
            ...{
              error: data.error,
              message: data.message,
            },
          });
        },
        requestId,
      );
    }
  };

  const showUrlOps = () => content.source === "s3";

  const autoDetect = () => content.source === "auto";

  const onValueChange = (value, tag) => {
    setContent({ ...content, [tag]: value });
    const error = !(
      `${value}`.startsWith("s3://") &&
      `${value}`.includes(".") &&
      `${value}`.split(".").length >= 2
    );
    if (tag === "thumbnail") setThumbError(error);
    if (tag === "url") setUrlError(error);
  };

  const onShowPropsChange = (value, tag) => {
    const _props = { ...showProps, [tag]: value };
    setShowProps(_props);
    if (["segment", "episode"].includes(tag)) {
      onValueChange(
        `${_props.code.toUpperCase()}${_props.season}${_props.episode > 9 ? _props.episode : `0${_props.episode}`}${_props.segment ? _props.segment : ""}`,
        "code",
      );
    }
  };

  const checkPropsValidity = () => {
    let basic =
      content.title !== null &&
      content.section !== null &&
      content.section.length > 0 &&
      content.language !== null &&
      content.language.length > 0 &&
      content.fromAge !== null &&
      +`${content.fromAge}` >= 0 &&
      content.toAge !== null &&
      +`${content.toAge}` <= 100 && 
      content.sourceType !== null && 
      content.sourceType.length > 0;

    basic =
      basic &&
      +`${content.fromAge}` < +`${content.toAge}` &&
      content.code &&
      content.code.length > 0 &&
      content.subject &&
      content.subject.length > 0;

    const isS3Validsource =
      content.source === "s3" &&
      files.length === 0 &&
      content.url !== null &&
      content.thumbnail !== null &&
      !thumbError &&
      !urlError;

    const isFileValidSource = files.length === 2 && content.source !== "s3";

    const isYoutubeValid =
      content.forYoutube === null
        ? true
        : content.forYoutube !== null &&
          content.forYoutube.channel !== null &&
          content.forYoutube.fade !== null;

    basic = basic && (isS3Validsource || isFileValidSource) && isYoutubeValid;

    return basic;
  };

  const getSectionTitle = () => {
    let _sections = sections || [];
    if (!_sections.length) return "";
    _sections = _sections.filter((section) => section.uuid === content.section);
    if (!_sections.length) return "";
    return _sections[0].title;
  };

  return (
    <EditPage
      title={uuid ? `Edit content` : `Add content`}
      status={status}
      btnLabel={canCreate ? (uuid ? "Update Now" : "Add Now") : null}
      onSave={() => createUpdate()}
      spacing={5}
      onBack={() => navigate(`${AppRoutes.contents}`, true)}
      validated={
        checkPropsValidity() && !status.loading && validateYoutubeProps()
      }
      onFinish={() => {
        setStatus({ ...{ loading: false, message: null, error: false } });
        if (!status.error) {
          setContent({ ..._ref });
          setRequestId(null);
          navigate(`${AppRoutes.contents}`, true);
        }
      }}
    >
      <Grid item md={2} xs={12}>
        <FormControl sx={{ minWidth: 50 }} fullWidth>
          <InputLabel id="content-source">Content Source</InputLabel>
          <Select
            value={content.source || "s3"}
            label="Content Source"
            labelId="content-source"
            id="source"
            disabled={status.loading}
            onChange={(e) => onValueChange(e.target.value, "source")}
          >
            {[
              { name: "S3 URLS", value: "s3" },
              { name: "File Upload", value: "file" },
            ].map((souces) => {
              return (
                <MenuItem value={souces.value} id={souces.value}>
                  &nbsp;&nbsp;&nbsp;{souces.name}
                  <br />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Grid>

      <Grid item md={2} xs={12}>
        <FormControl sx={{ minWidth: 50 }} fullWidth>
          <InputLabel id="content-source-types">Content Source Type</InputLabel>
          <Select
            value={content.sourceType}
            label="Content Source Type"
            labelId="content-source-types"
            id="source-type"
            disabled={status.loading}
            onChange={(e) => onValueChange(e.target.value, "sourceType")}
          >
            {contentSourceTypes.map((souces) => {
              return (
                <MenuItem value={souces.uuid} id={souces.uuid}>
                  &nbsp;&nbsp;&nbsp;{capitalizeFirstLetter(souces.label)}
                  <br />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Grid>

      {!autoDetect() && (
        <Grid item md={6} xs={12}>
          <EasyTextEdit
            label="Content title"
            placeholder="Descriptive tile of this content"
            type="text"
            id="title"
            errorText=""
            textField={true}
            disabled={status.loading}
            value={content.title}
            onChange={onValueChange}
          />
        </Grid>
      )}

      {!autoDetect() && (
        <Grid item md={2} xs={12}>
          <FormControl sx={{ minWidth: 50 }} fullWidth>
            <InputLabel id="subj">Content Subject</InputLabel>
            <Select
              value={content.subject}
              label="Content Subject"
              labelId="subj"
              id="subject"
              disabled={status.loading}
              onChange={(e) => onValueChange(e.target.value, "subject")}
            >
              {subjects.map((sub) => {
                return (
                  <MenuItem value={sub.uuid}>
                    &nbsp;&nbsp;&nbsp;{sub.name}
                    <br />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      )}

      {showUrlOps() && !autoDetect() && (
        <Grid item md={6} xs={12}>
          <EasyTextEdit
            placeholder="S3 file URI i.e s3://bucket-id/folder/directory/image.jpg"
            label="Content thumbnail image"
            type="text"
            textField={true}
            error={thumbError}
            id="thumbnail"
            errorText={
              thumbError ? "Make sure you fill in AWS s3 file URI" : ""
            }
            disabled={status.loading}
            value={content.thumbnail}
            onChange={onValueChange}
          />
        </Grid>
      )}

      {showUrlOps() && !autoDetect() && (
        <Grid item md={6} xs={12}>
          <EasyTextEdit
            placeholder="S3 file URI i.e s3://bucket-id/folder/directory/video.mp4"
            label="Actual content url (Image, Video, Audio, PDF)"
            type="text"
            textField={true}
            error={urlError}
            id="url"
            errorText={urlError ? "Make sure you fill in AWS s3 file URI" : ""}
            disabled={status.loading}
            value={content.url}
            onChange={onValueChange}
          />
        </Grid>
      )}

      {content.source === "file" &&
        getMimeType().length >= 2 &&
        !autoDetect() && (
          <Grid item md={12} xs={12}>
            <Dropzone
              onChange={updateFiles}
              value={files}
              minHeight="195px"
              maxFiles={2}
              accept={getMimeType().join(",")}
              maxFileSize={1024 * 1024 * 1024 * 5}
              label="Drag'n drop thumbnail and content here or click to browse"
            >
              {files.map((file) => (
                <FileMosaic
                  {...file}
                  resultOnTooltip
                  alwaysActive
                  preview
                  info
                  onDelete={onDelete}
                  onSee={handleSee}
                  onWatch={handleWatch}
                />
              ))}
            </Dropzone>

            <FullScreen
              open={imageSrc !== undefined}
              onClose={() => setImageSrc(undefined)}
            >
              <ImagePreview src={imageSrc} />
            </FullScreen>
            <FullScreen
              open={videoSrc !== undefined}
              onClose={() => setVideoSrc(undefined)}
            >
              <VideoPreview src={videoSrc} autoPlay controls />
            </FullScreen>
          </Grid>
        )}

      {!autoDetect() && (
        <Grid item md={4} xs={12}>
          <EasyTextEdit
            placeholder="Ordering preference"
            label="Order"
            type="text"
            id="order"
            textField={true}
            errorText=""
            disabled={status.loading}
            value={content.order}
            onChange={onValueChange}
          />
        </Grid>
      )}

      {!autoDetect() && (
        <Grid item md={4} xs={12}>
          <FormControl sx={{ minWidth: 200 }} fullWidth>
            <InputLabel id="type-label">Content type</InputLabel>
            <Select
              value={content.category}
              label="Content Type"
              labelId="type-label"
              id="category"
              autoWidth
              disabled={status.loading}
              onChange={(e) => {
                onValueChange(e.target.value, "category");
              }}
            >
              {(categories || []).map((category) => {
                return (
                  <MenuItem value={category.uuid} id={category.uuid}>
                    &nbsp;&nbsp;&nbsp;{category.name}
                    <br />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      )}

      {content.category && !autoDetect() && (
        <Grid item md={4} xs={12}>
          <Autocomplete
            id="section"
            disabled={status.loading}
            limitTags={2}
            value={getSectionTitle()}
            options={(sections || [])
              .filter((section) => section.category.uuid === content.category)
              .map((section) => section.title)}
            renderInput={(params) => (
              <TextField {...params} label="Content Section" />
            )}
            onChange={(_, value) => {
              const section = (sections || []).filter(
                (section) => section.title === value,
              )[0];
              if (section) onValueChange(section.uuid, "section");
            }}
          />
        </Grid>
      )}

      {!autoDetect() && (
        <Grid item md={12}>
          <Typography variant="h6" component="h2">
            Show details
          </Typography>
        </Grid>
      )}

      {!autoDetect() && (
        <Grid item md={3} xs={12}>
          <FormControl
            disabled={status.loading || shows.length === 0}
            sx={{ minWidth: 50 }}
            fullWidth
          >
            <InputLabel id="show">Content Show</InputLabel>
            <Select
              value={showProps.code}
              label="Content Show"
              labelId="show"
              id="show"
              onChange={(e) => onShowPropsChange(e.target.value, "code")}
            >
              {shows.map((show) => {
                return (
                  <MenuItem value={show.code} id={show.code}>
                    &nbsp;&nbsp;&nbsp;{show.name}
                    <br />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      )}

      {!autoDetect() && (
        <Grid item md={3} xs={12}>
          <FormControl
            disabled={status.loading || !showProps.code}
            sx={{ minWidth: 50 }}
            fullWidth
          >
            <InputLabel id="season">Show Season</InputLabel>
            <Select
              value={showProps.season}
              label="Content Season"
              labelId="season"
              id="season"
              onChange={(e) => onShowPropsChange(e.target.value, "season")}
            >
              {Array.from({
                length:
                  shows.find((show) => show.code === showProps.code)?.seasons ||
                  0,
              }).map((value, index) => {
                return (
                  <MenuItem value={index + 1} id={index + 1}>
                    &nbsp;&nbsp;&nbsp;Season {index + 1}
                    <br />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      )}

      {!autoDetect() && (
        <Grid item md={3} xs={12}>
          <FormControl
            disabled={status.loading || !showProps.season}
            sx={{ minWidth: 50 }}
            fullWidth
          >
            <InputLabel id="episode">Season Episode</InputLabel>
            <Select
              value={showProps.episode}
              label="Content Episode"
              labelId="episode"
              id="episode"
              onChange={(e) => onShowPropsChange(e.target.value, "episode")}
            >
              {Array.from({
                length:
                  shows.find((show) => show.code === showProps.code)
                    ?.episodes || 0,
              }).map((value, index) => {
                return (
                  <MenuItem value={index + 1} id={index + 1}>
                    &nbsp;&nbsp;&nbsp;Episode {index + 1}
                    <br />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      )}

      {!autoDetect() && (
        <Grid item md={3} xs={12}>
          <FormControl
            disabled={status.loading || !showProps.episode}
            sx={{ minWidth: 50 }}
            fullWidth
          >
            <InputLabel id="segment">Episode Segment</InputLabel>
            <Select
              value={showProps.segment}
              label="Content Segment"
              labelId="segment"
              id="segment"
              onChange={(e) => onShowPropsChange(e.target.value, "segment")}
            >
              {(
                shows.find((show) => show.code === showProps.code)?.segments ||
                []
              ).map((segment) => {
                return (
                  <MenuItem value={segment.code} id={segment.code}>
                    &nbsp;&nbsp;&nbsp;{segment.name}
                    <br />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      )}

      {!autoDetect() && (
        <Grid item md={12}>
          <Typography variant="h6" component="h2">
            Langauges, Audiance and Paywall
          </Typography>
        </Grid>
      )}

      {!autoDetect() && (
        <Grid item md={2} xs={12}>
          <FormControl
            disabled={status.loading}
            sx={{ minWidth: 50 }}
            fullWidth
          >
            <InputLabel id="language">Content Language</InputLabel>
            <Select
              value={content.language}
              label="Content Language"
              labelId="language"
              id="language"
              onChange={(e) => onValueChange(e.target.value, "language")}
            >
              {languages.map((language) => {
                return (
                  <MenuItem value={language.isoCode} id={language.isoCode}>
                    &nbsp;&nbsp;&nbsp;{language.name}
                    <br />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      )}

      {!autoDetect() && (
        <Grid item md={6} xs={12}>
          <Autocomplete
            multiple={true}
            id="age-group"
            disableCloseOnSelect
            limitTags={2}
            disabled={status.loading}
            value={ageRange}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="filled"
                  label={
                    ageGroup.filter((group) => group.range === option)[0].name
                  }
                  {...getTagProps({ index })}
                />
              ))
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {ageGroup.filter((group) => group.range === option)[0].name}
              </li>
            )}
            options={ageGroup.map((option) => option.range)}
            renderInput={(params) => (
              <TextField {...params} label="Target audiance" />
            )}
            onChange={(event, value) => {
              setAgeRange(value);
              const ranges = value.length
                ? getSingleRange(value).split("-")
                : [null, null];
              setContent({ ...content, fromAge: ranges[0], toAge: ranges[1] });
            }}
          />
        </Grid>
      )}

      {!autoDetect() && (
        <Grid item md={4} xs={12}>
          <Autocomplete
            multiple
            id="premium"
            disableCloseOnSelect
            disabled={status.loading}
            limitTags={2}
            value={
              content.premium && Array.isArray(content.premium)
                ? content.premium.map((pre) => pre.name)
                : []
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option}
              </li>
            )}
            options={continents.map((option) => option.name)}
            renderInput={(params) => (
              <TextField {...params} label="Premium Content Locations" />
            )}
            onChange={(event, value) => {
              const values = continents.filter((cont) =>
                value.includes(cont.name),
              );
              onValueChange(values, "premium");
            }}
          />
        </Grid>
      )}

      {!autoDetect() && (
        <Grid item md={12}>
          <Typography variant="h6" component="h2">
            {isVideo() ? "Streaming qualities & Trademarking" : " "}
          </Typography>
        </Grid>
      )}

      {!autoDetect() && isVideo() && (
        <Grid item md={noBranding() ? 4 : 3} xs={12}>
          <Autocomplete
            multiple
            id="qualities"
            disableCloseOnSelect
            limitTags={noBranding() ? 2 : 1}
            value={content.streamQuality}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="filled"
                  label={`${option}p`}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {`${option}p - ${option === "2160" ? "4K" : option === "1080" ? "UHD" : option === "720" ? "HD" : "SD"}`}
              </li>
            )}
            options={["240", "360", "480", "720", "1080", "2160"]}
            renderInput={(params) => (
              <TextField {...params} label={`Streaming quality`} />
            )}
            onChange={(event, value) => {
              onValueChange(value, "streamQuality");
            }}
          />
        </Grid>
      )}
      {!autoDetect() && isVideo() && (
        <Grid item md={noBranding() ? 3 : 3} xs={12}>
          <FormControl
            disabled={status.loading}
            sx={{ minWidth: 50 }}
            fullWidth
            error={checkLogoChennelError()}
          >
            <InputLabel id="show">Branding from</InputLabel>
            <Select
              value={noBranding() ? "none" : content.logoRef}
              label="Branding from"
              labelId="show"
              id="logoRef"
              onChange={(e) => {
                onValueChange(e.target.value, "logoRef");
              }}
            >
              {[{ uuid: "none", name: "No-Branding" }, ...tvShows].map((tv) => {
                return (
                  <MenuItem value={tv.uuid} id={`tv-${tv.uuid}`}>
                    &nbsp;&nbsp;&nbsp;{tv.name}
                    <br />
                  </MenuItem>
                );
              })}
            </Select>
            {checkLogoChennelError() &&
              content.logoRef !== content.forYoutube.channel && (
                <FormHelperText>
                  TV show and Channel must be the same
                </FormHelperText>
              )}
          </FormControl>
        </Grid>
      )}

      {!autoDetect() && !noBranding() && isVideo() && (
        <Grid item sx={{ pt: 1 }} md={noBranding() ? 1 : 2} xs={12}>
          <FormControlLabel
            checked={content.withIntroOutro ?? false}
            control={<Switch />}
            disabled={status.loading}
            onChange={(event) =>
              onValueChange(event.target.checked, "withIntroOutro")
            }
            label={<Typography variant="body1">Intro/Outro</Typography>}
          />
        </Grid>
      )}

      {!autoDetect() && isVideo() && (
        <Grid item md={12}>
          <Typography variant="h6" component="h2">
            Uploading options
          </Typography>
        </Grid>
      )}

      {!autoDetect() && (
        <Grid item md={6} xs={12}>
          <FormControlLabel
            checked={content.forTheApp}
            control={<Switch />}
            disabled={status.loading}
            onChange={(event) => {
              onValueChange(event.target.checked, "forTheApp");
              setSchedule(false);
            }}
            label={<Typography variant="body1">Upload to the app</Typography>}
          />

          {content.forTheApp && (
            <Grid container sx={{ mt: 2 }} spacing={2}>
              <Grid item md={6} xs={12}>
                <FormControlLabel
                  checked={schedule}
                  disabled={status.loading}
                  control={<Switch />}
                  onChange={(event) => {
                    setSchedule(event.target.checked);
                    if (!event.target.checked) onValueChange("", "scheduledAt");
                  }}
                  label={
                    <Typography variant="body1">Schedule content</Typography>
                  }
                />
              </Grid>
              {schedule && (
                <Grid item md={6} xs={12}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DesktopDateTimePicker
                      label={"Goes live at"}
                      ampm={false}
                      value={date}
                      disabled={status.loading}
                      renderInput={(props) => (
                        <TextField
                          {...props}
                          size="large"
                          variant="outlined"
                          className={appStyle.inputs}
                          helperText={null}
                        />
                      )}
                      onChange={(value) => {
                        onValueChange(
                          toTzTime(value.toDate()).format(),
                          "scheduledAt",
                        );
                        setDate(value.toDate());
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      )}

      {!autoDetect() && isVideo() && (
        <Grid item md={6} xs={12}>
          <FormControlLabel
            checked={content.forYoutube !== null}
            control={<Switch />}
            disabled={status.loading}
            onChange={(event) => {
              onValueChange(
                event.target.checked ? { uploaded: false } : null,
                "forYoutube",
              );
            }}
            label={<Typography variant="body1">Upload to Youtube</Typography>}
          />

          {content.forYoutube !== null && (
            <Grid container sx={{ mt: 2 }} spacing={2}>
              <Grid item md={6} xs={12}>
                <FormControl
                  disabled={status.loading}
                  sx={{ minWidth: 50 }}
                  fullWidth
                >
                  <InputLabel id="channels">Channels</InputLabel>
                  <Select
                    value={content.forYoutube.channel}
                    label="Channel"
                    labelId="channels"
                    id="channels"
                    onChange={(e) =>
                      onValueChange(
                        {
                          ...content.forYoutube,
                          channel: e.target.value,
                        },
                        "forYoutube",
                      )
                    }
                  >
                    {channels.map((channel) => {
                      return (
                        <MenuItem value={channel.uuid} id={channel.uuid}>
                          &nbsp;&nbsp;&nbsp;{channel.name}
                          <br />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item md={6} xs={12}>
                <FormControl
                  disabled={status.loading}
                  sx={{ minWidth: 50 }}
                  fullWidth
                >
                  <InputLabel id="fades">Transition Fade</InputLabel>
                  <Select
                    value={content.forYoutube.fade}
                    label="Transition Fade"
                    labelId="fades"
                    id="fades"
                    onChange={(e) =>
                      onValueChange(
                        {
                          ...content.forYoutube,
                          fade: e.target.value,
                        },
                        "forYoutube",
                      )
                    }
                  >
                    {[true, false].map((fade) => {
                      return (
                        <MenuItem value={fade} id={fade}>
                          &nbsp;&nbsp;&nbsp;{fade ? "White fade" : "Black fade"}
                          <br />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </EditPage>
  );
};

export default ContentEdit;
